// The part-of-speech + grammar field config — the JS mirror of the backend POS_LAYOUTS / FieldSchema.
// Central so adding a POS layout is a one-line change (same idea as the presenter). Drives the editor
// forms AND the preview rebuild, so both label grammar fields identically.
import type {
  OptionSelectGroup,
  OptionSelectOption,
} from "@love-irish/resource-codex-meta-components"

export const POS_OPTIONS = [
  "noun",
  "verb",
  "adjective",
  "adverb",
  "preposition",
  "pronoun",
  "conjunction",
  "interjection",
  "numeral",
  "determiner",
] as const

// Proper-noun kind for a noun sense — the single source for proper-noun display (a name is still a
// noun). "" = an ordinary common noun. Mirrors the backend ENTITY_TYPES; shown as a teaching OptionSelect.
export const ENTITY_TYPE_OPTIONS: OptionSelectOption[] = [
  { value: "", label: "Common noun", description: "An ordinary noun, not a specific name." },
  { value: "name", label: "Name", description: "A person's name (Saoirse, Cú Chulainn)." },
  { value: "place", label: "Place", description: "A town, county, country, or landmark." },
  { value: "brand", label: "Brand", description: "A product or brand name." },
  { value: "organisation", label: "Org", description: "A company, group, or institution." },
  { value: "event", label: "Event", description: "A festival or event (Samhain, Nollaig)." },
]

export const REGISTER_OPTIONS = [
  "standard",
  "formal",
  "informal",
  "colloquial",
  "literary",
  "archaic",
]

// Registers grouped by usage, with a blurb per option, so the picker teaches what each one means.
// Shaped as OptionSelect groups (label + description + options) so it passes straight to <OptionSelect>.
export const REGISTER_GROUPS: OptionSelectGroup[] = [
  {
    label: "Everyday",
    description: "Common speech and writing.",
    options: [
      { value: "standard", label: "Standard", description: "The caighdeán, the official standard." },
      { value: "informal", label: "Informal", description: "Casual, everyday speech." },
      { value: "colloquial", label: "Colloquial", description: "Slang or regional casual usage." },
    ],
  },
  {
    label: "Formal & literary",
    description: "Elevated, written, or older usage.",
    options: [
      { value: "formal", label: "Formal", description: "Polite or official contexts." },
      { value: "literary", label: "Literary", description: "An elevated, written style." },
      { value: "archaic", label: "Archaic", description: "Older forms, rare in modern use." },
    ],
  },
]

export interface GrammarField {
  key: string
  label: string
  type: "text" | "select"
  /** Dictionary shorthand for the field name (e.g. "gs.", "pl."), shown on compact cards. Mirrors FieldSchema. */
  short?: string
  /** `description` teaches what a choice means (OptionSelect); `valueShort` is the value's abbreviation
   *  (e.g. gender m./f.), shown instead of the full value on compact cards. */
  options?: Array<{ value: string; label: string; description?: string; valueShort?: string }>
}

/** Which grammar fields each POS surfaces, in order (mirrors the backend POS_LAYOUTS). */
export const POS_GRAMMAR: Record<string, GrammarField[]> = {
  noun: [
    {
      key: "gender",
      label: "Gender",
      type: "select",
      short: "gen.",
      options: [
        { value: "masculine", label: "Masculine", valueShort: "m" },
        { value: "feminine", label: "Feminine", valueShort: "f" },
      ],
    },
    {
      key: "declension",
      label: "Declension",
      type: "select",
      short: "decl.",
      options: [1, 2, 3, 4, 5].map((n) => ({ value: String(n), label: String(n) })),
    },
    { key: "genitive_singular", label: "Genitive singular", type: "text", short: "gs." },
    { key: "nominative_plural", label: "Nominative plural", type: "text", short: "pl." },
    { key: "genitive_plural", label: "Genitive plural", type: "text", short: "gpl." },
  ],
  verb: [
    {
      key: "conjugation_class",
      label: "Conjugation class",
      type: "select",
      short: "conj.",
      options: [
        {
          value: "first",
          label: "First",
          description: "One-syllable verb stems (e.g. rith, ól). The most common pattern.",
        },
        {
          value: "second",
          label: "Second",
          description: "Longer stems, usually ending in -igh or -aigh (e.g. ceannaigh, imigh).",
        },
        {
          value: "irregular",
          label: "Irregular",
          description: "One of the eleven irregular verbs, learned individually (e.g. bí, déan).",
        },
      ],
    },
    { key: "verbal_noun", label: "Verbal noun", type: "text", short: "vn." },
    { key: "verbal_adjective", label: "Verbal adjective", type: "text", short: "va." },
  ],
  adjective: [
    {
      key: "gender",
      label: "Gender",
      type: "select",
      short: "gen.",
      options: [
        { value: "masculine", label: "Masculine", valueShort: "m" },
        { value: "feminine", label: "Feminine", valueShort: "f" },
      ],
    },
    {
      key: "declension",
      label: "Declension",
      type: "select",
      short: "decl.",
      options: [1, 2, 3].map((n) => ({ value: String(n), label: String(n) })),
    },
    { key: "genitive_singular", label: "Genitive singular", type: "text", short: "gs." },
  ],
}

/** Present tense (an aimsir láithreach) persons — the paradigm shown for verbs, keyed by person. */
export const VERB_PERSONS = [
  { key: "i", label: "mé (I)" },
  { key: "you", label: "tú (you)" },
  { key: "he", label: "sé (he)" },
  { key: "she", label: "sí (she)" },
  { key: "we", label: "muid (we)" },
  { key: "you_pl", label: "sibh (you pl.)" },
  { key: "they", label: "siad (they)" },
]
